'use client';

import React from 'react';
import { useTranslation } from 'react-i18next';
import { TrendingUp, TrendingDown, Minus, Percent, BarChart3, ShoppingCart, CheckCircle } from 'lucide-react';

import { MetricCard } from './MetricCard';
import { ChartContainer } from './ChartContainer';
import { EmptyState } from './EmptyState';
import { useConversionOverTimeData } from '@/lib/api/analytics';
import { useStatsFilters } from '../context/StatsFiltersContext';

interface ConversionOverTimeProps {
    className?: string;
}

export function ConversionOverTime({ className = '' }: ConversionOverTimeProps) {
    const { selectedDates, selectedStores, selectedCurrency, selectedGranularity } = useStatsFilters();
    const { t } = useTranslation('analytics');

    const { data, isLoading, error } = useConversionOverTimeData(
        {
            startDate: selectedDates.from!,
            endDate: selectedDates.to!,
            storeIds: selectedStores,
            currency: selectedCurrency,
            granularity: selectedGranularity as 'daily' | 'weekly' | 'monthly',
        },
        {
            enabled: !!(selectedDates.from && selectedDates.to),
        }
    );

    // Transform data for the chart
    const chartData = (data || []).map((point) => ({
        code: typeof point.period === 'string' ? point.period.split('T')[0] : point.period,
        value: point.conversionRate,
        volume: point.conversions,
    }));

    // Calculate summary statistics
    const hasData = chartData.length > 0;
    const totalCheckouts = (data || []).reduce((sum, point) => sum + point.totalCheckouts, 0);
    const totalConversions = (data || []).reduce((sum, point) => sum + point.conversions, 0);
    const avgConversionRate = totalCheckouts > 0 ? (totalConversions / totalCheckouts) * 100 : 0;

    // Calculate trend (compare last period to average)
    let trendDirection: 'up' | 'down' | 'neutral' = 'neutral';
    let trendValue = 0;
    if (chartData.length >= 2) {
        const lastPeriodRate = chartData[chartData.length - 1].value;
        const previousPeriodsAvg = chartData.slice(0, -1).reduce((sum, p) => sum + p.value, 0) / (chartData.length - 1);
        trendValue = lastPeriodRate - previousPeriodsAvg;
        if (trendValue > 0.5) trendDirection = 'up';
        else if (trendValue < -0.5) trendDirection = 'down';
    }

    if (error) {
        return (
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-6 ${className}`}>
                <MetricCard title={t('conversion.overTime.title')} description={t('conversion.overTime.description')}>
                    <div className="flex items-center justify-center h-full">
                        <p className="text-destructive">{t('conversion.overTime.errorMessage')}</p>
                    </div>
                </MetricCard>
            </div>
        );
    }

    return (
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-6 ${className}`}>
            <MetricCard
                title={t('conversion.overTime.title')}
                description={t('conversion.overTime.chartDescription')}
                isLoading={isLoading}
            >
                {!hasData ? (
                    <EmptyState message={t('conversion.overTime.emptyState')} />
                ) : (
                    <ChartContainer
                        data={chartData}
                        chartType="line"
                        isPercentage={true}
                        emptyStateMessage={t('conversion.overTime.chartEmptyState')}
                    />
                )}
            </MetricCard>

            {/* Summary Stats */}
            <MetricCard
                title={t('conversion.overTime.summary.title')}
                description={t('conversion.overTime.summary.description')}
                isLoading={isLoading}
                dynamicHeight
            >
                {!hasData ? (
                    <EmptyState message={t('conversion.overTime.summary.emptyState')} />
                ) : (
                    <div className="grid grid-cols-2 gap-4">
                        {/* Average Conversion Rate - Primary metric */}
                        <div className="p-4 bg-primary/10 rounded-lg border border-primary/20">
                            <div className="flex items-center gap-2 mb-2">
                                <div className="p-1.5 bg-primary/20 rounded">
                                    <Percent className="h-3.5 w-3.5 text-primary" />
                                </div>
                                <span className="text-xs font-medium text-muted-foreground">{t('conversion.overTime.avgRate')}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-2xl font-semibold text-foreground">{avgConversionRate.toFixed(1)}%</span>
                                {trendDirection === 'up' && <TrendingUp className="h-4 w-4 text-green-500" />}
                                {trendDirection === 'down' && <TrendingDown className="h-4 w-4 text-red-500" />}
                                {trendDirection === 'neutral' && <Minus className="h-4 w-4 text-muted-foreground" />}
                            </div>
                        </div>

                        {/* Period Trend */}
                        <div className="p-4 bg-muted/50 rounded-lg border border-border">
                            <div className="flex items-center gap-2 mb-2">
                                <div className="p-1.5 bg-muted rounded">
                                    <BarChart3 className="h-3.5 w-3.5 text-muted-foreground" />
                                </div>
                                <span className="text-xs font-medium text-muted-foreground">{t('conversion.overTime.latestVsAvg')}</span>
                            </div>
                            <div className="flex items-center gap-1">
                                <span className={`text-2xl font-semibold ${trendValue >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {trendValue >= 0 ? '+' : ''}{trendValue.toFixed(1)}%
                                </span>
                            </div>
                        </div>

                        {/* Total Checkouts */}
                        <div className="p-4 bg-muted/50 rounded-lg border border-border">
                            <div className="flex items-center gap-2 mb-2">
                                <div className="p-1.5 bg-muted rounded">
                                    <ShoppingCart className="h-3.5 w-3.5 text-muted-foreground" />
                                </div>
                                <span className="text-xs font-medium text-muted-foreground">{t('conversion.overTime.totalCheckouts')}</span>
                            </div>
                            <p className="text-2xl font-semibold text-foreground">{totalCheckouts.toLocaleString()}</p>
                        </div>

                        {/* Total Conversions */}
                        <div className="p-4 bg-muted/50 rounded-lg border border-border">
                            <div className="flex items-center gap-2 mb-2">
                                <div className="p-1.5 bg-muted rounded">
                                    <CheckCircle className="h-3.5 w-3.5 text-muted-foreground" />
                                </div>
                                <span className="text-xs font-medium text-muted-foreground">{t('conversion.overTime.totalOrders')}</span>
                            </div>
                            <p className="text-2xl font-semibold text-foreground">{totalConversions.toLocaleString()}</p>
                        </div>
                    </div>
                )}
            </MetricCard>
        </div>
    );
}
